import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Input,
  Switch,
} from '@wove/web-ui'
import { CargowiseOrganization } from '@wove/api'

const organizationSchema = z.object({
  cargowiseOrganizationCode: z.string().min(1, 'Organization code is required'),
  name: z.string().min(1, 'Organization name is required'),
  isActive: z.boolean(),
})

type FormData = z.infer<typeof organizationSchema>

interface CargowiseOrganizationFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organization?: CargowiseOrganization | null
  onSubmit: (data: FormData) => Promise<void>
  isSubmitting?: boolean
}

export default function CargowiseOrganizationFormDialog({
  open,
  onOpenChange,
  organization,
  onSubmit,
  isSubmitting = false,
}: CargowiseOrganizationFormDialogProps) {
  const isEditing = !!organization

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      cargowiseOrganizationCode: '',
      name: '',
      isActive: true,
    },
  })

  // Reset form when organization changes or dialog opens
  useEffect(() => {
    if (open) {
      if (organization) {
        reset({
          cargowiseOrganizationCode: organization.cargowiseOrganizationCode,
          name: organization.name,
          isActive: organization.isActive,
        })
      } else {
        reset({
          cargowiseOrganizationCode: '',
          name: '',
          isActive: true,
        })
      }
    }
  }, [open, organization, reset])

  const handleFormSubmit = async (data: FormData) => {
    await onSubmit(data)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Organization' : 'Create Organization'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the organization details below.'
                : 'Enter the details for the new CargoWise organization.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="code">Organization Code</Label>
              <Input
                id="code"
                {...register('cargowiseOrganizationCode')}
                placeholder="e.g., US001"
                disabled={isEditing}
              />
              {errors.cargowiseOrganizationCode && (
                <p className="text-sm text-red-600">{errors.cargowiseOrganizationCode.message}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input id="name" {...register('name')} placeholder="e.g., United States Branch" />
              {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="active">Active</Label>
              <Switch
                id="active"
                checked={watch('isActive')}
                onCheckedChange={(checked) => setValue('isActive', checked)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? isEditing
                  ? 'Updating...'
                  : 'Creating...'
                : isEditing
                ? 'Update'
                : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
